import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { openNotification } from 'components/notification';
import { ApiDocServiceCustomFieldsRepository } from 'services/custom-fields/ApiDocServiceCustomFieldsRepository';
import { type AssignedCustomField, type CustomField } from 'services/custom-fields/schemas';
import { QueryKeys } from 'utils/queryKeys';
import { MutationKeys } from 'utils/mutationKeys';
import { DeletedCustomFieldError } from 'services/errors/DeletedCustomFieldError';

const docServiceCustomFieldsRepository = new ApiDocServiceCustomFieldsRepository();

export type AssignCustomFieldMutation = ReturnType<typeof useAssignCustomFieldMutation>;

export function useAssignCustomFieldMutation(contentId: string, isTemplate = false) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const assignedKey = QueryKeys.editorAssignedCustomFields(contentId);

  return useMutation({
    mutationKey: MutationKeys.assignCustomField,

    onMutate: async (field) => {
      await queryClient.cancelQueries({ queryKey: assignedKey });

      const previous = queryClient.getQueryData<AssignedCustomField[]>(assignedKey);

      queryClient.setQueryData<AssignedCustomField[]>(assignedKey, (old = []) => [
        ...old,
        {
          id: `optimistic-${field.id}`,
          name: field.name,
          friendlyName: field.friendlyName,
          type: field.fieldType,
          options: field.options,
          isRequired: false,
          path: `All/Documents/${field.friendlyName}`,
          value: null,
        },
      ]);
      return { previous };
    },

    mutationFn: async (field: CustomField) => {
      return await docServiceCustomFieldsRepository.assignCustomField(contentId, field, isTemplate);
    },

    onError: (error, _field, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(assignedKey, ctx.previous);
      }
      queryClient.invalidateQueries({ queryKey: QueryKeys.customFields });
      openNotification({
        type: 'error',
        title: t(
          error instanceof DeletedCustomFieldError
            ? 'editor.variables.assign_deleted_custom_field_error'
            : 'editor.variables.assign_custom_field_error'
        ),
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignedKey });
    },
  });
}
